/**
 * Sales / Purchase invoice arithmetic, as the Accounts prototype works it out:
 * line amount = qty × rate less discount; GST per line on a sale, per rate on a purchase;
 * CGST + SGST inside the company's state, IGST outside it; an unregistered supplier
 * charges no GST; round off to the rupee; TDS sec 1031 (old 194Q) at 0.1% on the part
 * of a supplier's purchases this year above ₹50 lakh.
 *
 * The same file is kept in frontend/src/lib/tradeCalc.ts so the form shows exactly
 * what saving posts — a test checks the two stay identical.
 */

export const GST_RATES = [0, 5, 12, 18, 28];
export const TDS_1031 = { rate: 0.1, above: 5_000_000 };

export interface TradeLineIn { qty: number; rate: number; disc?: number; gst: number }
export interface TradeCalcOpts {
  /** Party in the company's own state → CGST + SGST; otherwise IGST. */
  intra: boolean;
  /** Purchase: the supplier has a GSTIN (no GST on an unregistered supplier's bill). */
  registered?: boolean;
  roundOff: boolean;
  /** Purchase: deduct TDS 1031, given this supplier's purchases (taxable) earlier this year. */
  tds?: { on: boolean; before: number };
}

const r2 = (x: number) => Math.round(x * 100) / 100;

export function tradeCalc(kind: 'Sales' | 'Purchase', items: TradeLineIn[], o: TradeCalcOpts) {
  const lines = items.map((l) => ({ ...l, amt: r2(l.qty * l.rate * (1 - (l.disc || 0) / 100)) }));
  let cgst = 0, sgst = 0, igst = 0;
  const add = (t: number) => {
    if (o.intra) { const c = r2(t / 2); cgst = r2(cgst + c); sgst = r2(sgst + t - c); } else igst = r2(igst + t);
  };
  if (kind === 'Sales') {
    for (const l of lines) add(r2((l.amt * l.gst) / 100));
  } else if (o.registered !== false) {
    const byRate = new Map<number, number>();
    for (const l of lines) byRate.set(l.gst, r2((byRate.get(l.gst) ?? 0) + l.amt));
    for (const [g, tx] of byRate) add(r2((tx * g) / 100));
  }
  const taxable = r2(lines.reduce((s, l) => s + l.amt, 0));
  const total = r2(taxable + cgst + sgst + igst);
  const R = o.roundOff ? Math.round(total) : total;
  const ro = r2(R - total);

  let tds = 0, tdsBase = 0, tdsReason = '';
  if (kind === 'Purchase' && o.tds?.on) {
    const before = o.tds.before, cum = r2(before + taxable);
    tdsBase = r2(Math.max(0, cum - Math.max(before, TDS_1031.above)));
    tds = Math.round((tdsBase * TDS_1031.rate) / 100);
    tdsReason = tdsBase > 0
      ? (before < TDS_1031.above ? 'Crossed ₹50 L this bill – TDS on the excess' : 'Year-to-date above ₹50 L')
      : `Year-to-date ₹${cum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} – below ₹50 L`;
  }
  return { lines, taxable, cgst, sgst, igst, total, R, ro, tds, tdsBase, tdsReason, payable: r2(R - tds) };
}
